import { useState, useMemo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
  Database,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'

interface RegistrosTableProps {
  data: Record<string, unknown>[]
  columns: string[]
  columnLabels?: Record<string, string>
  nullLabels?: Record<string, string>
  totalRegistros: number
  page: number
  pageSize: number
  totalPages: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  onSort?: (column: string, direction: 'asc' | 'desc') => void
  onSearch?: (searchTerm: string) => void
  searchTerm?: string
  sortColumn?: string
  sortDirection?: 'asc' | 'desc'
  isLoading?: boolean
}

export function RegistrosTable({
  data,
  columns,
  columnLabels = {},
  nullLabels = {},
  totalRegistros,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onSort,
  onSearch,
  searchTerm: externalSearchTerm,
  sortColumn,
  sortDirection,
  isLoading = false,
}: RegistrosTableProps) {
  const [localSearchTerm, setLocalSearchTerm] = useState(externalSearchTerm || '')
  const [qrModal, setQrModal] = useState<{ src: string; titulo: string; x: number; y: number } | null>(null)

  // Búsqueda manual: se ejecuta al presionar el botón o Enter
  const handleSearchSubmit = () => {
    onSearch?.(localSearchTerm)
  }

  // Si hay onSearch, el filtrado se hace en el servidor, si no, filtrar localmente
  const filteredData = useMemo(() => {
    // Si hay búsqueda del servidor, los datos ya vienen filtrados
    if (onSearch) return data
    // Filtrado local (fallback)
    if (!localSearchTerm) return data
    return data.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(localSearchTerm.toLowerCase())
      )
    )
  }, [data, localSearchTerm, onSearch])

  const handleSort = (column: string) => {
    if (!onSort) return
    const newDirection =
      sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
    onSort(column, newDirection)
  }

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="h-3 w-3 text-primary" />
    ) : (
      <ArrowDown className="h-3 w-3 text-primary" />
    )
  }

  const formatCellValue = (value: unknown, column?: string): string => {
    const isEmpty = value === null || value === undefined || value === ''
    if (isEmpty) return column && nullLabels[column] ? nullLabels[column] : '-'
    if (typeof value === 'boolean') return value ? 'Sí' : 'No'
    if (value instanceof Date) {
      return value.toLocaleDateString('es-EC')
    }
    // Detectar si es una fecha ISO
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      try {
        return new Date(value).toLocaleDateString('es-EC', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch {
        return String(value)
      }
    }
    return String(value)
  }

  return (
    <>
    <Card className="border border-gray-300 dark:border-gray-700 w-full">
      <CardHeader className="py-2 px-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-1.5 text-sm shrink-0">
            <Database className="h-3.5 w-3.5" />
            Registros
            <span className="text-xs font-normal text-muted-foreground">
              ({totalRegistros.toLocaleString('es-EC')})
            </span>
          </CardTitle>
          <div className="flex items-center gap-1.5">
            <div className="relative flex-1 min-w-[120px] max-w-[180px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <Input
                placeholder="Buscar..."
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearchSubmit()
                }}
                className="pl-7 h-7 text-xs w-full"
              />
            </div>
            <Button
              type="button"
              size="sm"
              onClick={handleSearchSubmit}
              className="h-7 px-2 text-xs shrink-0"
            >
              <Search className="h-3 w-3 mr-1" />
              Buscar
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-2 pt-0">
        {isLoading ? (
          <div className="h-[400px] flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">
              Cargando registros...
            </div>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Database className="h-12 w-12 mb-3 text-muted-foreground/30" />
            <p className="text-muted-foreground font-medium">No se encontraron registros</p>
            {localSearchTerm && (
              <p className="text-sm text-muted-foreground">
                Intenta con otros términos de búsqueda
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="rounded-md border overflow-hidden w-full">
              <div className="overflow-x-auto w-full">
                <Table className="text-xs w-full table-auto">
                  <TableHeader>
                    <TableRow className="bg-gray-200 dark:bg-[hsl(220,15%,16%)]">
                      {columns.map((column) => (
                        <TableHead
                          key={column}
                          className={cn(
                            'font-semibold whitespace-nowrap py-1.5 px-2 text-xs h-auto',
                            onSort && 'cursor-pointer hover:bg-muted transition-colors'
                          )}
                          onClick={() => handleSort(column)}
                        >
                          <div className="flex items-center gap-1">
                            {columnLabels[column] || column}
                            {onSort && getSortIcon(column)}
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((row, rowIndex) => (
                      <TableRow
                        key={rowIndex}
                        className="hover:bg-muted/30 transition-colors"
                      >
                        {columns.map((column) => {
                          const cellValue = formatCellValue(row[column], column)
                          const isUrlQr = column.toLowerCase().includes('url') || column.toLowerCase().includes('qr')

                          return (
                            <TableCell
                              key={column}
                              className="whitespace-nowrap py-1 px-2"
                              style={{ maxWidth: isUrlQr ? '60px' : '200px' }}
                            >
                              {isUrlQr ? (
                                <div className="flex items-center justify-center">
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide border transition-colors bg-blue-500/10 border-blue-500/30 text-blue-600 hover:bg-blue-500/20 dark:text-blue-400 cursor-pointer"
                                    onClick={(e) => {
                                      const nombre = String(row['nombre'] ?? row['Nombre'] ?? '')
                                      const apellido = String(row['apellido'] ?? row['Apellido'] ?? '')
                                      const titulo = `QR Evento ${nombre} ${apellido}`.trim()
                                      setQrModal({ src: cellValue, titulo, x: e.clientX, y: e.clientY })
                                    }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <rect width="5" height="5" x="3" y="3" rx="1"/><rect width="5" height="5" x="16" y="3" rx="1"/><rect width="5" height="5" x="3" y="16" rx="1"/><path d="M21 16h-3a2 2 0 0 0-2 2v3"/><path d="M21 21v.01"/><path d="M12 7v3a2 2 0 0 1-2 2H7"/><path d="M3 12h.01"/><path d="M12 3h.01"/><path d="M12 16v.01"/><path d="M16 12h1"/><path d="M21 12v.01"/><path d="M12 21v-1"/>
                                    </svg>
                                    QR
                                  </button>
                                </div>
                              ) : (
                                <span className="block truncate">
                                  {cellValue}
                                </span>
                              )}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Paginación */}
            <div className="flex items-center justify-between flex-wrap gap-2 mt-2">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => onPageSizeChange(Number(value))}
                >
                  <SelectTrigger className="w-14 h-6 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 25, 50, 100].map((size) => (
                      <SelectItem key={size} value={String(size)} className="text-xs">
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span>/ pág</span>
              </div>

              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">
                  {page}/{totalPages}
                </span>
                <div className="flex items-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => onPageChange(1)}
                    disabled={page === 1}
                  >
                    <ChevronsLeft className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => onPageChange(page + 1)}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => onPageChange(totalPages)}
                    disabled={page === totalPages}
                  >
                    <ChevronsRight className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>

    {qrModal && (
      <>
        <div className="fixed inset-0 z-[9998]" onClick={() => setQrModal(null)} />
        <div
          className="fixed z-[9999] rounded-lg border border-border shadow-xl overflow-hidden bg-background flex flex-col"
          style={{
            left: qrModal.x + 16,
            top: qrModal.y - 210,
            width: 200,
          }}
        >
          <div className="px-2 py-1.5 text-[11px] font-medium text-foreground bg-muted border-b border-border flex items-center justify-between gap-1">
            <span className="truncate">{qrModal.titulo}</span>
            <button
              type="button"
              onClick={() => setQrModal(null)}
              className="shrink-0 text-muted-foreground hover:text-foreground transition-colors leading-none"
            >
              ×
            </button>
          </div>
          <img
            src={qrModal.src}
            alt={qrModal.titulo}
            className="w-full object-contain"
            style={{ height: 180 }}
          />
          <div className="px-2 py-1.5 border-t border-border bg-muted">
            <a
              href={qrModal.src}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1 text-[10px] font-medium text-blue-600 dark:text-blue-400 hover:underline"
            >
              Abrir en nueva pestaña
            </a>
          </div>
        </div>
      </>
    )}
    </>
  )
}
